import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt'; // Import Portuguese locale file
import 'dayjs/locale/ar'; // Import Arabic locale file
import { useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';

interface ITimePickerProps {
  time: string;
  handleOneTimeChange: (_field: string, _value: string) => void;
  locale?: string;
  minTime?: string;
  maxTime?: string;
  digitalTime?: boolean;
  availabilitiesError?: boolean;
  hasPermissions?: boolean;
}

export default function TimePicker({
  time,
  handleOneTimeChange,
  locale = 'en',
  minTime,
  maxTime,
  digitalTime = false, // default to false
  availabilitiesError,
  hasPermissions,
}: ITimePickerProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  // Set dayjs locale based on the provided locale prop
  dayjs.locale(locale);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DesktopTimePicker
        onChange={(newValue: Dayjs | null) => {
          // here format in 24 hours
          const formattedTime = dayjs(newValue).format('HH:mm');
          handleOneTimeChange('time', formattedTime);
        }}
        defaultValue={null}
        value={time === '' ? null : dayjs(time, 'HH:mm')}
        minTime={minTime ? dayjs(minTime, 'HH:mm') : undefined} // Set minTime if provided
        maxTime={maxTime ? dayjs(maxTime, 'HH:mm') : undefined}
        views={['hours', 'minutes']}
        ampm={!digitalTime}
        disabled={!hasPermissions}
        slotProps={{
          textField: {
            variant: 'outlined',
            fullWidth: true,
            onClick: (e) => e.preventDefault(),
            onKeyDown: (e) => e.preventDefault(),
            error: availabilitiesError,
            helperText: availabilitiesError
              ? t('bookings.setAvailabilityStep.invalidTimeRange')
              : '',

            sx: {
              '& .MuiOutlinedInput-root': {
                height: '38px',
                width: !isSmallScreen ? '178px' : '155px',
                '& fieldset': {
                  padding: '12px',
                  border: '1px solid rgba(124, 174, 190, 0.50)',
                  color: 'primary',
                  backgroundColor: 'transparent',
                  borderRadius: '10px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(124, 174, 190, 0.50)', // Border color on hover
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(124, 174, 190, 0.50)', // Blue border when focused
                },
                '&.Mui-error fieldset': {
                  borderColor: theme.palette.error.main, // Red border if there's an error
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#000',
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}
